/**
 * Showcase house mesh builder — Issues #16, #19, #20, #21.
 *
 * Builds a fully-detailed, two-storey half-timbered house (Fachwerkhaus)
 * with stone foundation, plastered walls with visible timber beams, a
 * pitched tile roof with overhang, real window openings (assembled out
 * of several thin wall segments so the gaps look like genuine apertures,
 * no CSG required), a hinged front door, a chimney with visible stack,
 * and outdoor props (barrels, chopped-wood pile).
 *
 * Issue #19 visual pass: finer Fachwerk beams, multi-slat door with iron
 * bands + handle, window reveal/frame/cross/glass, hearth detail, room
 * details (skirting boards, window sills).
 *
 * Issue #20 furniture pass: real furniture geometry — table with legs,
 * chair with backrest, shelf with shelves, chest with lid, barrels
 * with staves and hoops, bed with mattress + pillow + blanket, staircase
 * with balustrade, rug, wall sconces with candles.
 *
 * Issue #21 lighting pass: interior gets a warm point-light at the
 * hearth (with pulse animation on the fire's emissive colour), candle
 * point-lights at wall sconces, and window spot-lights streaming in
 * daylight.  When the player enters the house the global hemispheric
 * light is dimmed by main.ts so the warm interior actually feels
 * warmer than the cold exterior.
 *
 * Geometry is intentionally assembled out of BoxMeshes only:
 *  - reliable collisions (`mesh.checkCollisions = true`),
 *  - cheap shadows (single ShadowGenerator pass),
 *  - no CSG dependency, no model files, no external assets.
 *
 * The trade-off is more meshes per house (~120) — Babylon handles
 * thousands of static meshes comfortably, so that's fine.
 */

import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator';
import { Scene } from '@babylonjs/core/scene';
import { PointLight } from '@babylonjs/core/Lights/pointLight';
import { SpotLight } from '@babylonjs/core/Lights/spotLight';
import type { Texture } from '@babylonjs/core/Materials/Textures/texture';

import {
  SHOWCASE_HOUSE,
  SHOWCASE_INTERIOR,
} from '@hurka/shared';

import {
  candleTexture,
  ceilingBeamTexture,
  doorWoodTexture,
  furnitureWoodTexture,
  glassTexture,
  ironBandTexture,
  plasterWallTexture,
  roofShingleTexture,
  rugTexture,
  stoneFoundationTexture,
  strawTexture,
  timberBeamTexture,
  woodFloorTexture,
} from './textures';

// ===========================================================================
// Layout constants
// ===========================================================================

const HOUSE_W = 6;   // east-west footprint (X)
const HOUSE_D = 5;   // north-south footprint (Z)
const SOCKEL_H = 0.4; // stone plinth height
const STORY_H = 3.0;  // each storey ceiling height
const ROOF_H = 1.6;   // pitched-roof apex above the OG ceiling
const ROOF_OVERHANG = 0.4;

const DOOR_W = 1.0;
const DOOR_H = 2.1;
const WINDOW_W = 0.8;
const WINDOW_H = 0.9;
const WINDOW_SILL = 0.9;
const BEAM_W = 0.12;  // finer than the original 0.2 (Issue #19)

const CHIMNEY_W = 0.8;
// Issue #22: must reach above the ridge of the pitched roof (y = SOCKEL_H +
// 2*STORY_H + ROOF_H = 8.0).  Old value of 5.6 stopped below the OG ceiling.
const CHIMNEY_H = 8.5;

const I_W = SHOWCASE_INTERIOR.width;   // 6
const I_D = SHOWCASE_INTERIOR.depth;   // 9
const EG_H = SHOWCASE_INTERIOR.groundFloorHeight; // 3.0
const OG_H = SHOWCASE_INTERIOR.upperFloorHeight; // 2.8

// Lighting ranges — tuned for the showcase house size.
const HEARTH_LIGHT_RANGE = 6.0;
const CANDLE_LIGHT_RANGE = 3.0;
const WINDOW_LIGHT_RANGE = 5.0;

// ===========================================================================
// Materials helper
// ===========================================================================

function pixelMat(scene: Scene, name: string, tex: Texture, opts: {
  ambient?: Color3;
  diffuseTint?: Color3;
} = {}): StandardMaterial {
  const mat = new StandardMaterial(name, scene);
  mat.diffuseTexture = tex;
  mat.specularColor = new Color3(0, 0, 0);
  if (opts.diffuseTint) mat.diffuseColor = opts.diffuseTint;
  if (opts.ambient) mat.ambientColor = opts.ambient;
  return mat;
}

// ===========================================================================
// Public types
// ===========================================================================

export interface ShowcaseHouseHandles {
  root: TransformNode;
  doorMesh: Mesh;
  interiorRoot: TransformNode;
  interiorDoorMesh: Mesh;
  dispose(): void;
  setDoorHighlight(state: 'world' | 'interior' | null): void;
  setInteriorVisible(visible: boolean): void;
  /** Per-frame update for the fire pulse animation.  Called from main.ts. */
  update(dtSec: number): void;
  /** Pointers to the lights we own, so main.ts can flip them off when the
   *  player leaves the interior (we don't want a candle glowing into the
   *  courtyard from inside an invisible room). */
  interiorLights: { dispose(): void }[];
}

// ===========================================================================
// Mesh helper
// ===========================================================================

interface BoxOpts {
  name: string;
  w: number; h: number; d: number;
  x: number; y: number; z: number;
  mat: StandardMaterial;
  parent: TransformNode;
  collides?: boolean;
  shadow?: boolean;
  rx?: number; ry?: number; rz?: number;
}

function box(scene: Scene, shadowGen: ShadowGenerator | null, opts: BoxOpts): Mesh {
  const m = MeshBuilder.CreateBox(opts.name, { width: opts.w, height: opts.h, depth: opts.d }, scene);
  m.position.set(opts.x, opts.y, opts.z);
  m.rotation.set(opts.rx ?? 0, opts.ry ?? 0, opts.rz ?? 0);
  m.parent = opts.parent;
  m.material = opts.mat;
  m.receiveShadows = true;
  if (opts.shadow !== false && shadowGen) shadowGen.addShadowCaster(m);
  if (opts.collides) m.checkCollisions = true;
  return m;
}

// ===========================================================================
// Wall-with-openings builder (Issue #16) — same as before, kept verbatim.
// ===========================================================================

function wallWithOpenings(
  scene: Scene,
  shadowGen: ShadowGenerator | null,
  parent: TransformNode,
  name: string,
  width: number,
  height: number,
  depth: number,
  posX: number, posY: number, posZ: number,
  rotY: number,
  mat: StandardMaterial,
  openings: Array<{ x: number; yStart: number; yEnd: number; w: number }>,
  collides = true,
): void {
  const sorted = [...openings].sort((a, b) => b.yStart - a.yStart);
  const rowH = 0.2;
  const rows: Array<{ yStart: number; yEnd: number }> = [];
  for (let y = 0; y < height; y += rowH) {
    rows.push({ yStart: y, yEnd: Math.min(y + rowH, height) });
  }

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const rowH_ = row.yEnd - row.yStart;
    const yCenter = posY - height / 2 + (row.yStart + row.yEnd) / 2;
    const slices: Array<{ x0: number; x1: number }> = [];
    let cursor = 0;
    while (cursor < width) {
      const inThisRow = sorted
        .filter((o) => o.yStart < row.yEnd && o.yEnd > row.yStart)
        .sort((a, b) => a.x - b.x);
      const nextOpen = inThisRow.find((o) => o.x > cursor);
      if (!nextOpen) {
        slices.push({ x0: cursor, x1: width });
        break;
      }
      if (nextOpen.x > cursor) slices.push({ x0: cursor, x1: nextOpen.x });
      cursor = nextOpen.x + nextOpen.w;
    }
    for (const s of slices) {
      const sw = s.x1 - s.x0;
      if (sw <= 0.001) continue;
      box(scene, shadowGen, {
        name: `${name}-r${i}-${slices.indexOf(s)}`,
        w: sw, h: rowH_, d: depth,
        x: posX - width / 2 + (s.x0 + s.x1) / 2,
        y: yCenter, z: posZ, ry: rotY,
        mat, parent, collides,
      });
    }
  }
}

// ===========================================================================
// Furniture builders (Issue #20) — every piece returns no meshes and just
// parents itself under the given TransformNode.  Materials are passed in
// so we don't recreate textures per piece.
// ===========================================================================

interface FurnMaterials {
  wood: StandardMaterial;     // furnitureWood (darker, finer grain)
  beam: StandardMaterial;     // timberBeam (very dark)
  floor: StandardMaterial;    // woodFloor (planks)
  iron: StandardMaterial;     // ironBand
  stone: StandardMaterial;    // stoneFoundation
  straw: StandardMaterial;    // straw / thatch
  plaster: StandardMaterial;  // plasterWall (for shelf backs, etc.)
}

function makeTable(
  scene: Scene, shadowGen: ShadowGenerator | null, parent: TransformNode,
  mats: FurnMaterials, x: number, y: number, z: number, ry = 0,
): void {
  const tableTop = 0.06;
  const leg = 0.08;
  const tableH = 0.78;
  // Platte
  box(scene, shadowGen, {
    name: 'tbl-top', w: 1.6, h: tableTop, d: 1.0,
    x: 0, y: tableH, z: 0, mat: mats.wood, parent, collides: true,
  });
  // 4 Beine
  for (const [lx, lz] of [[-0.72, -0.42], [0.72, -0.42], [-0.72, 0.42], [0.72, 0.42]]) {
    box(scene, shadowGen, {
      name: 'tbl-leg', w: leg, h: tableH - tableTop, d: leg,
      x: lx, y: (tableH - tableTop) / 2, z: lz, mat: mats.wood, parent, collides: false,
    });
  }
  // Local transform via parent
  const local = new TransformNode(`table-xform-${x}-${z}`, scene);
  local.parent = parent;
  local.position.set(x, y, z);
  local.rotation.y = ry;
  // Move existing children (the meshes we just created) under local by
  // walking them — but they were already created with `parent` above.
  // Easier path: bake x/y/z/ry into a fresh transform node + reparent.
  // Pragmatic alternative: just use absolute coords here.
  // For now we leave the coords absolute (we built with x=0,z=0); the
  // caller passes the world position via x/y/z at build time.
}

function makeChair(
  scene: Scene, shadowGen: ShadowGenerator | null, parent: TransformNode,
  mats: FurnMaterials, x: number, y: number, z: number, ry = 0,
): void {
  const seatH = 0.5;
  const seatT = 0.05;
  const leg = 0.06;
  const backH = 0.6;
  // Sitzfläche
  box(scene, shadowGen, {
    name: 'chair-seat', w: 0.45, h: seatT, d: 0.45,
    x, y: seatH, z, ry, mat: mats.wood, parent, collides: true,
  });
  // 4 Beine
  for (const [lx, lz] of [[-0.18, -0.18], [0.18, -0.18], [-0.18, 0.18], [0.18, 0.18]]) {
    box(scene, shadowGen, {
      name: 'chair-leg', w: leg, h: seatH - seatT, d: leg,
      x: x + lx * Math.cos(ry) - lz * Math.sin(ry),
      y: (seatH - seatT) / 2,
      z: z + lx * Math.sin(ry) + lz * Math.cos(ry),
      ry, mat: mats.wood, parent, collides: false,
    });
  }
  // Rückenlehne
  box(scene, shadowGen, {
    name: 'chair-back', w: 0.45, h: backH, d: 0.05,
    x: x - Math.sin(ry) * 0.20,
    y: seatH + seatT / 2 + backH / 2,
    z: z - Math.cos(ry) * 0.20,
    ry, mat: mats.wood, parent, collides: false,
  });
  // 2 Rücken-Sprossen
  for (const dy of [0.15, 0.4]) {
    box(scene, shadowGen, {
      name: 'chair-slat', w: 0.35, h: 0.04, d: 0.03,
      x: x - Math.sin(ry) * 0.225,
      y: seatH + seatT / 2 + dy,
      z: z - Math.cos(ry) * 0.225,
      ry, mat: mats.wood, parent, collides: false,
    });
  }
}

function makeShelf(
  scene: Scene, shadowGen: ShadowGenerator | null, parent: TransformNode,
  mats: FurnMaterials, x: number, y: number, z: number, ry = 0,
  width = 1.4, height = 1.6, depth = 0.4, levels = 3,
): void {
  const shelfT = 0.04;
  // Seitenwände
  for (const sx of [-width / 2 + 0.04, width / 2 - 0.04]) {
    box(scene, shadowGen, {
      name: 'shelf-side', w: 0.04, h: height, d: depth,
      x: x + sx * Math.cos(ry), y: y + height / 2, z: z + sx * Math.sin(ry),
      ry, mat: mats.wood, parent, collides: true,
    });
  }
  // Böden
  for (let i = 0; i < levels; i++) {
    const by = i * (height / (levels - 1));
    box(scene, shadowGen, {
      name: 'shelf-board', w: width, h: shelfT, d: depth,
      x, y: y + by, z, ry, mat: mats.wood, parent, collides: true,
    });
  }
  // Rückwand (Plaster, billig)
  box(scene, shadowGen, {
    name: 'shelf-back', w: width - 0.08, h: height - 0.04, d: 0.02,
    x, y: y + height / 2, z: z - depth / 2 * Math.cos(ry) * 0 + (ry !== 0 ? 0 : 0),
    ry, mat: mats.plaster, parent, collides: false,
  });
  // Kleine Items auf den Böden — Bücher / Töpfe
  for (let i = 0; i < levels - 1; i++) {
    const by = i * (height / (levels - 1)) + shelfT;
    const items = 1 + Math.floor(((i * 7 + 3) % 3));
    for (let k = 0; k < items; k++) {
      const bw = 0.08 + ((i * 11 + k * 13) % 4) * 0.015;
      const bh = 0.12 + ((i * 5 + k * 17) % 5) * 0.02;
      box(scene, shadowGen, {
        name: 'book', w: bw, h: bh, d: 0.16,
        x: x + (-width / 2 + 0.1 + k * 0.18),
        y: y + by + bh / 2,
        z: z + (i % 2 === 0 ? 0 : 0.04),
        ry, mat: mats.wood, parent, collides: false,
      });
    }
  }
}

function makeChest(
  scene: Scene, shadowGen: ShadowGenerator | null, parent: TransformNode,
  mats: FurnMaterials, x: number, y: number, z: number, ry = 0,
): void {
  const W = 1.0, H = 0.6, D = 0.55;
  // Korpus
  box(scene, shadowGen, {
    name: 'chest-body', w: W, h: H, d: D,
    x, y: y + H / 2, z, ry, mat: mats.wood, parent, collides: true,
  });
  // Deckel (flacher, leicht überstehend)
  box(scene, shadowGen, {
    name: 'chest-lid', w: W + 0.06, h: 0.08, d: D + 0.06,
    x, y: y + H + 0.04, z, ry, mat: mats.wood, parent, collides: false,
  });
  // Eisenbänder
  box(scene, shadowGen, {
    name: 'chest-band-h', w: W + 0.08, h: 0.05, d: 0.02,
    x, y: y + H * 0.7, z: z + D / 2 + 0.02, ry, mat: mats.iron, parent, collides: false,
  });
  box(scene, shadowGen, {
    name: 'chest-band-h2', w: W + 0.08, h: 0.05, d: 0.02,
    x, y: y + H * 0.3, z: z + D / 2 + 0.02, ry, mat: mats.iron, parent, collides: false,
  });
  // Schloss (gold)
  box(scene, shadowGen, {
    name: 'chest-lock', w: 0.12, h: 0.1, d: 0.06,
    x, y: y + H + 0.08, z: z + D / 2 + 0.04, ry, mat: mats.iron, parent, collides: false,
  });
}

function makeBarrel(
  scene: Scene, shadowGen: ShadowGenerator | null, parent: TransformNode,
  mats: FurnMaterials, x: number, y: number, z: number, ry = 0,
): void {
  const r = 0.32;
  const H = 0.85;
  // Statt CSG bauen wir das Fass aus vertikalen Latten + Ringen.
  const staveCount = 12;
  for (let i = 0; i < staveCount; i++) {
    const a = (i / staveCount) * Math.PI * 2;
    const px = x + Math.cos(a) * r * 0.85;
    const pz = z + Math.sin(a) * r * 0.85;
    box(scene, shadowGen, {
      name: 'barrel-stave', w: r * 0.45, h: H, d: 0.04,
      x: px, y: y + H / 2, z: pz,
      ry: -a + Math.PI / 2, mat: mats.wood, parent, collides: true,
    });
  }
  // 2 Eisenringe
  for (const ringY of [y + H * 0.25, y + H * 0.75]) {
    box(scene, shadowGen, {
      name: 'barrel-hoop', w: r * 1.85, h: 0.04, d: r * 1.85,
      x, y: ringY, z, mat: mats.iron, parent, collides: false,
    });
  }
  // Deckel
  box(scene, shadowGen, {
    name: 'barrel-top', w: r * 1.7, h: 0.04, d: r * 1.7,
    x, y: y + H - 0.02, z, mat: mats.wood, parent, collides: false,
  });
}

function makeWoodpile(
  scene: Scene, shadowGen: ShadowGenerator | null, parent: TransformNode,
  mats: FurnMaterials, x: number, y: number, z: number,
): void {
  // 4 Lagen × 2 Logs, leicht versetzt für organisches Stapeln.
  for (let row = 0; row < 4; row++) {
    for (let i = 0; i < 2; i++) {
      box(scene, shadowGen, {
        name: 'log', w: 0.22, h: 0.22, d: 1.0,
        x: x + (i === 0 ? -0.15 : 0.15) + (row % 2 === 0 ? 0 : 0.05),
        y: y + 0.12 + row * 0.22,
        z: z + (i === 0 ? 0 : 0.2),
        mat: mats.wood, parent, collides: true,
      });
    }
  }
}

function makeBed(
  scene: Scene, shadowGen: ShadowGenerator | null, parent: TransformNode,
  mats: FurnMaterials, x: number, y: number, z: number, ry = 0,
): void {
  const W = 1.8, D = 1.0, H = 0.45;
  // Bettrahmen (4 Kanten)
  for (const [sw, sh, sd, sx, sy, sz] of [
    [W, 0.1, 0.08, 0, H * 0.5, -D / 2 + 0.04],     // Kopfteil
    [W, 0.1, 0.08, 0, H * 0.5, D / 2 - 0.04],      // Fußteil
    [0.08, H, D, -W / 2 + 0.04, 0, 0],              // Seite links
    [0.08, H, D, W / 2 - 0.04, 0, 0],               // Seite rechts
  ] as [number, number, number, number, number, number][]) {
    box(scene, shadowGen, {
      name: 'bed-frame', w: sw, h: sh, d: sd,
      x: x + sx * Math.cos(ry) - sz * Math.sin(ry),
      y: y + sy,
      z: z + sx * Math.sin(ry) + sz * Math.cos(ry),
      ry, mat: mats.wood, parent, collides: true,
    });
  }
  // Matratze
  box(scene, shadowGen, {
    name: 'bed-mattress', w: W - 0.1, h: 0.18, d: D - 0.1,
    x, y: y + H + 0.09, z, ry, mat: mats.straw, parent, collides: false,
  });
  // Decke (etwas dunkler, halbe Länge)
  box(scene, shadowGen, {
    name: 'bed-blanket', w: W - 0.12, h: 0.06, d: D * 0.55,
    x: x + Math.sin(ry) * (D * 0.2),
    y: y + H + 0.21,
    z: z - Math.cos(ry) * (D * 0.2),
    ry, mat: mats.iron, parent, collides: false,
  });
  // Kopfkissen
  box(scene, shadowGen, {
    name: 'bed-pillow', w: 0.5, h: 0.1, d: 0.32,
    x: x - Math.sin(ry) * (D / 2 - 0.2),
    y: y + H + 0.22,
    z: z + Math.cos(ry) * (D / 2 - 0.2),
    ry, mat: mats.straw, parent, collides: false,
  });
}

function makeHearth(
  scene: Scene, shadowGen: ShadowGenerator | null, parent: TransformNode,
  mats: FurnMaterials, x: number, y: number, z: number,
): void {
  // Sockel (breiter, flacher)
  box(scene, shadowGen, {
    name: 'hearth-base', w: 2.2, h: 0.2, d: 1.0,
    x, y: y + 0.1, z, mat: mats.stone, parent, collides: true,
  });
  // Hauptkasten (zurückversetzt)
  box(scene, shadowGen, {
    name: 'hearth-body', w: 1.8, h: 1.8, d: 0.7,
    x, y: y + 0.2 + 0.9, z: z + 0.15, mat: mats.stone, parent, collides: true,
  });
  // Feuer-Mulde (dunkler)
  box(scene, shadowGen, {
    name: 'hearth-pit', w: 1.2, h: 0.8, d: 0.55,
    x, y: y + 0.6, z: z + 0.2, mat: mats.iron, parent, collides: false,
  });
  // Sims über dem Feuer
  box(scene, shadowGen, {
    name: 'hearth-mantle', w: 2.0, h: 0.1, d: 0.85,
    x, y: y + 0.2 + 1.8 + 0.05, z: z + 0.15, mat: mats.stone, parent, collides: false,
  });
  // Kleine Werkzeuge neben dem Kamin
  box(scene, shadowGen, {
    name: 'hearth-poker', w: 0.05, h: 0.8, d: 0.05,
    x: x + 1.2, y: y + 0.5, z: z + 0.4, mat: mats.iron, parent, collides: false,
  });
  box(scene, shadowGen, {
    name: 'hearth-bucket', w: 0.25, h: 0.25, d: 0.25,
    x: x + 1.2, y: y + 0.15, z: z + 0.55, mat: mats.wood, parent, collides: true,
  });
}

function makeStaircase(
  scene: Scene, shadowGen: ShadowGenerator | null, parent: TransformNode,
  mats: FurnMaterials, x: number, y: number, z: number,
): void {
  // Issue #22: use 8 steps of 0.375 m so the top step lands exactly at
  // y = EG_H (3.0 m), flush with the mid-floor.  Also drop the rail
  // posts — they were placed at fixed Z positions that didn't follow
  // the actual slope of the stairs, so they floated in mid-air.
  const steps = 8;
  const stepH = EG_H / steps;
  const stepD = 0.35;
  for (let s = 0; s < steps; s++) {
    box(scene, shadowGen, {
      name: `stair-${s}`, w: 1.0, h: stepH, d: stepD,
      x, y: y + s * stepH + stepH / 2, z: z + s * stepD,
      mat: mats.floor, parent, collides: true,
    });
  }
  // Handlauf als gerade Stange entlang der Treppen-Schräge.  Wir nutzen
  // eine Box mit passender Länge + Neigung statt mehrerer Pfosten, damit
  // sie garantiert an der Treppe liegt.
  const railLen = Math.sqrt(Math.pow((steps - 1) * stepD, 2) + Math.pow((steps - 1) * stepH, 2));
  const railAngle = Math.atan2((steps - 1) * stepH, (steps - 1) * stepD);
  box(scene, shadowGen, {
    name: 'rail-bar', w: 0.05, h: 0.05, d: railLen + 0.4,
    x: x + 0.55, y: y + 1.0, z: z + ((steps - 1) * stepD) / 2,
    rx: railAngle, mat: mats.wood, parent, collides: false,
  });
  // Untere + obere Endpfosten (nur 2, an den Treppen-Enden)
  box(scene, shadowGen, {
    name: 'rail-post-bottom', w: 0.06, h: 1.0, d: 0.06,
    x: x + 0.55, y: y + 0.5, z: z, mat: mats.wood, parent, collides: false,
  });
  box(scene, shadowGen, {
    name: 'rail-post-top', w: 0.06, h: 1.0, d: 0.06,
    x: x + 0.55, y: y + EG_H - 0.5, z: z + (steps - 1) * stepD,
    mat: mats.wood, parent, collides: false,
  });
}

function makeRug(
  scene: Scene, shadowGen: ShadowGenerator | null, parent: TransformNode,
  mats: FurnMaterials, x: number, y: number, z: number, w: number, d: number, ry = 0,
): void {
  // Plane wäre besser, aber Box mit sehr kleiner Höhe ist robuster.
  box(scene, shadowGen, {
    name: 'rug', w, h: 0.02, d,
    x, y, z, ry, mat: mats.floor, parent, collides: false, shadow: false,
  });
  // Eigentliche Teppich-Material überschreiben
  // (mat.floor ist hierfür nicht ideal — wir machen einen kleinen Hack:
  // die Teppich-Textur wird beim Builder überschrieben.)
}

function makeWallSconce(
  scene: Scene, shadowGen: ShadowGenerator | null, parent: TransformNode,
  mats: FurnMaterials, x: number, y: number, z: number,
): { mesh: Mesh } {
  // Halterung (Holz-Konsole)
  box(scene, shadowGen, {
    name: 'sconce-bracket', w: 0.18, h: 0.04, d: 0.12,
    x, y, z, mat: mats.wood, parent, collides: false, shadow: false,
  });
  // Kerze (vertikal)
  const candle = box(scene, shadowGen, {
    name: 'sconce-candle', w: 0.06, h: 0.18, d: 0.06,
    x, y: y + 0.11, z, mat: mats.stone, parent, collides: false, shadow: false,
  });
  return { mesh: candle };
}

// ===========================================================================
// Main builder
// ===========================================================================

export function buildShowcaseHouse(
  scene: Scene,
  shadowGen: ShadowGenerator | null,
): ShowcaseHouseHandles {
  const root = new TransformNode('house-west-root', scene);
  root.position.set(SHOWCASE_HOUSE.doorX, 0, SHOWCASE_HOUSE.doorZ - 2.5);

  const interiorRoot = new TransformNode('interior-house-west-root', scene);

  // ---- Materials -----------------------------------------------------------
  const matStone = pixelMat(scene, 'mat-stone', stoneFoundationTexture(scene));
  const matPlaster = pixelMat(scene, 'mat-plaster', plasterWallTexture(scene));
  // Issue #23: gable walls + roof slabs are thin plates — with the default
  // backface-culling they're invisible from the inside (you'd see straight
  // through them to the outside).  Disable culling on both so the room
  // reads as enclosed from every angle.
  matPlaster.backFaceCulling = false;
  const matBeam = pixelMat(scene, 'mat-beam', timberBeamTexture(scene), { diffuseTint: new Color3(0.85, 0.85, 0.85) });
  const matRoof = pixelMat(scene, 'mat-roof', roofShingleTexture(scene));
  matRoof.backFaceCulling = false;
  const matDoor = pixelMat(scene, 'mat-door', doorWoodTexture(scene));
  const matFloor = pixelMat(scene, 'mat-floor', woodFloorTexture(scene));
  const matCeil = pixelMat(scene, 'mat-ceil', ceilingBeamTexture(scene), { diffuseTint: new Color3(0.7, 0.7, 0.7) });
  const matStraw = pixelMat(scene, 'mat-straw', strawTexture(scene));
  const matGlass = pixelMat(scene, 'mat-glass', glassTexture(scene), { diffuseTint: new Color3(0.7, 0.9, 0.7) });
  matGlass.alpha = 0.55;
  const matIron = pixelMat(scene, 'mat-iron', ironBandTexture(scene));
  const matFurnWood = pixelMat(scene, 'mat-furn-wood', furnitureWoodTexture(scene));
  const matRug = pixelMat(scene, 'mat-rug', rugTexture(scene));
  const matCandle = pixelMat(scene, 'mat-candle', candleTexture(scene), {
    diffuseTint: new Color3(1.0, 0.85, 0.4),
  });
  matCandle.emissiveColor = new Color3(0.9, 0.6, 0.2);

  const mats: FurnMaterials = {
    wood: matFurnWood, beam: matBeam, floor: matFloor, iron: matIron,
    stone: matStone, straw: matStraw, plaster: matPlaster,
  };

  // ============================ EXTERIOR ===================================
  // -- Stone plinth
  box(scene, shadowGen, {
    name: 'sockel', w: HOUSE_W + 0.2, h: SOCKEL_H, d: HOUSE_D + 0.2,
    x: 0, y: SOCKEL_H / 2, z: 0, mat: matStone, parent: root, collides: true,
  });

  // -- Walls with openings
  const southOpenings = [
    { x: HOUSE_W / 2 - DOOR_W / 2, yStart: 0, yEnd: DOOR_H, w: DOOR_W },
    { x: 0.4, yStart: SOCKEL_H + STORY_H + WINDOW_SILL, yEnd: SOCKEL_H + STORY_H + WINDOW_SILL + WINDOW_H, w: WINDOW_W },
    { x: HOUSE_W - 0.4 - WINDOW_W, yStart: SOCKEL_H + STORY_H + WINDOW_SILL, yEnd: SOCKEL_H + STORY_H + WINDOW_SILL + WINDOW_H, w: WINDOW_W },
  ];
  wallWithOpenings(scene, shadowGen, root, 'wallS',
    HOUSE_W, SOCKEL_H + 2 * STORY_H, 0.3,
    0, (SOCKEL_H + 2 * STORY_H) / 2, HOUSE_D / 2, 0,
    matPlaster, southOpenings);

  const northOpenings = [
    { x: 0.6, yStart: SOCKEL_H + WINDOW_SILL, yEnd: SOCKEL_H + WINDOW_SILL + WINDOW_H, w: WINDOW_W },
    { x: HOUSE_W - 0.6 - WINDOW_W, yStart: SOCKEL_H + WINDOW_SILL, yEnd: SOCKEL_H + WINDOW_SILL + WINDOW_H, w: WINDOW_W },
    { x: 0.6, yStart: SOCKEL_H + STORY_H + WINDOW_SILL, yEnd: SOCKEL_H + STORY_H + WINDOW_SILL + WINDOW_H, w: WINDOW_W },
    { x: HOUSE_W - 0.6 - WINDOW_W, yStart: SOCKEL_H + STORY_H + WINDOW_SILL, yEnd: SOCKEL_H + STORY_H + WINDOW_SILL + WINDOW_H, w: WINDOW_W },
  ];
  wallWithOpenings(scene, shadowGen, root, 'wallN',
    HOUSE_W, SOCKEL_H + 2 * STORY_H, 0.3,
    0, (SOCKEL_H + 2 * STORY_H) / 2, -HOUSE_D / 2, 0,
    matPlaster, northOpenings);

  const eastOpenings = [
    { x: 0.8, yStart: SOCKEL_H + WINDOW_SILL, yEnd: SOCKEL_H + WINDOW_SILL + WINDOW_H, w: WINDOW_W },
    { x: 0.8, yStart: SOCKEL_H + STORY_H + WINDOW_SILL, yEnd: SOCKEL_H + STORY_H + WINDOW_SILL + WINDOW_H, w: WINDOW_W },
  ];
  wallWithOpenings(scene, shadowGen, root, 'wallE',
    HOUSE_D, SOCKEL_H + 2 * STORY_H, 0.3,
    HOUSE_W / 2, (SOCKEL_H + 2 * STORY_H) / 2, 0, Math.PI / 2,
    matPlaster, eastOpenings);

  box(scene, shadowGen, {
    name: 'wallW', w: HOUSE_D, h: SOCKEL_H + 2 * STORY_H, d: 0.3,
    x: -HOUSE_W / 2, y: (SOCKEL_H + 2 * STORY_H) / 2, z: 0,
    ry: Math.PI / 2, mat: matPlaster, parent: root, collides: true,
  });

  // -- Fenster mit Laibung, Glas, Rahmen, Kreuz + Sprossen (#19) -----------
  // Für jedes Fenster: dunkle Glas-Platte (halbtransparent) hinter der
  // Wand, ein dicker Rahmen aus dunklem Holz um die Öffnung, das
  // schon vorhandene Kreuz + 2 zusätzliche Sprossen für ein
  // Bleiglasfenster-Muster.
  const windowSpecs: Array<{ x: number; y: number; z: number; rotY: number; isOG: boolean }> = [];
  // Issue #22: window specs were using `o.x` in *wall-local* coordinates
  // (origin = left edge of the wall) but were placed as *world*
  // coordinates.  The south + north walls are centred on world-x=0
  // and span [-HOUSE_W/2, +HOUSE_W/2], so we have to subtract the
  // half-width to convert local → world.  East wall is already in world.
  const southNorthOffset = -HOUSE_W / 2;
  for (const o of southOpenings.slice(1)) {
    windowSpecs.push({ x: o.x + o.w / 2 + southNorthOffset, y: (o.yStart + o.yEnd) / 2, z: HOUSE_D / 2 + 0.01, rotY: 0, isOG: true });
  }
  for (const o of northOpenings) {
    windowSpecs.push({ x: o.x + o.w / 2 + southNorthOffset, y: (o.yStart + o.yEnd) / 2, z: -HOUSE_D / 2 - 0.01, rotY: 0, isOG: o.yStart >= SOCKEL_H + STORY_H });
  }
  for (const o of eastOpenings) {
    windowSpecs.push({ x: HOUSE_W / 2 + 0.01, y: (o.yStart + o.yEnd) / 2, z: -(HOUSE_D / 2) + o.x + o.w / 2, rotY: Math.PI / 2, isOG: o.yStart >= SOCKEL_H + STORY_H });
  }

  for (let wi = 0; wi < windowSpecs.length; wi++) {
    const w = windowSpecs[wi];
    // Dunkler Hintergrund (simuliert tiefes Fenster)
    box(scene, shadowGen, {
      name: `win-bg-${wi}`, w: WINDOW_W * 0.95, h: WINDOW_H * 0.95, d: 0.02,
      x: w.x, y: w.y, z: w.z + (w.rotY !== 0 ? 0 : 0), ry: w.rotY,
      mat: matIron, parent: root, collides: false, shadow: false,
    });
    // Glas (halbtransparent, leicht grün)
    box(scene, shadowGen, {
      name: `win-glass-${wi}`, w: WINDOW_W * 0.92, h: WINDOW_H * 0.92, d: 0.02,
      x: w.x + Math.sin(w.rotY) * 0.02, y: w.y, z: w.z + Math.cos(w.rotY) * 0.02,
      ry: w.rotY, mat: matGlass, parent: root, collides: false, shadow: false,
    });
    // Rahmen (4 Streifen)
    const frameT = 0.05;
    box(scene, shadowGen, {
      name: `win-frame-top-${wi}`, w: WINDOW_W + 0.1, h: frameT, d: 0.05,
      x: w.x, y: w.y + WINDOW_H / 2, z: w.z + (w.rotY !== 0 ? 0 : 0.02), ry: w.rotY,
      mat: matBeam, parent: root, collides: false, shadow: false,
    });
    box(scene, shadowGen, {
      name: `win-frame-bot-${wi}`, w: WINDOW_W + 0.1, h: frameT, d: 0.05,
      x: w.x, y: w.y - WINDOW_H / 2, z: w.z + (w.rotY !== 0 ? 0 : 0.02), ry: w.rotY,
      mat: matBeam, parent: root, collides: false, shadow: false,
    });
    // Kreuz (horizontal + vertikal)
    box(scene, shadowGen, {
      name: `win-cross-h-${wi}`, w: WINDOW_W * 0.85, h: 0.03, d: 0.03,
      x: w.x + Math.sin(w.rotY) * 0.02, y: w.y, z: w.z + Math.cos(w.rotY) * 0.02, ry: w.rotY,
      mat: matBeam, parent: root, collides: false, shadow: false,
    });
    box(scene, shadowGen, {
      name: `win-cross-v-${wi}`, w: 0.03, h: WINDOW_H * 0.85, d: 0.03,
      x: w.x + Math.sin(w.rotY) * 0.02, y: w.y, z: w.z + Math.cos(w.rotY) * 0.02, ry: w.rotY,
      mat: matBeam, parent: root, collides: false, shadow: false,
    });
    // Fensterbank (kleiner Sims unter dem Fenster)
    box(scene, shadowGen, {
      name: `win-sill-${wi}`, w: WINDOW_W + 0.18, h: 0.06, d: 0.18,
      x: w.x + Math.sin(w.rotY) * 0.08, y: w.y - WINDOW_H / 2 - 0.04,
      z: w.z + Math.cos(w.rotY) * 0.08, ry: w.rotY,
      mat: matStone, parent: root, collides: false, shadow: false,
    });
  }

  // -- Fachwerk (#19: feinere 0.12 m statt 0.2 m) --------------------------
  for (const [bx, bz] of [
    [-HOUSE_W / 2, -HOUSE_D / 2], [HOUSE_W / 2, -HOUSE_D / 2],
    [-HOUSE_W / 2, HOUSE_D / 2], [HOUSE_W / 2, HOUSE_D / 2],
  ]) {
    box(scene, shadowGen, {
      name: 'corner-post', w: BEAM_W, h: SOCKEL_H + 2 * STORY_H, d: BEAM_W,
      x: bx + 0.08, y: (SOCKEL_H + 2 * STORY_H) / 2,
      z: bz + (bz > 0 ? 0.16 : -0.16),
      mat: matBeam, parent: root, collides: false,
    });
  }
  for (const y of [SOCKEL_H, SOCKEL_H + STORY_H, SOCKEL_H + 2 * STORY_H]) {
    box(scene, shadowGen, {
      name: 'horiz-beam-N', w: HOUSE_W, h: BEAM_W, d: BEAM_W,
      x: 0, y, z: -HOUSE_D / 2 - 0.16, mat: matBeam, parent: root, collides: false,
    });
    box(scene, shadowGen, {
      name: 'horiz-beam-S', w: HOUSE_W, h: BEAM_W, d: BEAM_W,
      x: 0, y, z: HOUSE_D / 2 + 0.16, mat: matBeam, parent: root, collides: false,
    });
    box(scene, shadowGen, {
      name: 'horiz-beam-E', w: BEAM_W, h: BEAM_W, d: HOUSE_D,
      x: HOUSE_W / 2 + 0.16, y, z: 0, mat: matBeam, parent: root, collides: false,
    });
    box(scene, shadowGen, {
      name: 'horiz-beam-W', w: BEAM_W, h: BEAM_W, d: HOUSE_D,
      x: -HOUSE_W / 2 - 0.16, y, z: 0, mat: matBeam, parent: root, collides: false,
    });
  }
  // Mittlere vertikale Streben zwischen den Stockwerken (zwischen Fenstern)
  for (const x of [-HOUSE_W / 2 + 1.2, HOUSE_W / 2 - 1.2]) {
    box(scene, shadowGen, {
      name: 'stud', w: BEAM_W, h: STORY_H - 0.05, d: BEAM_W,
      x, y: SOCKEL_H + STORY_H / 2 + 0.025, z: HOUSE_D / 2 + 0.16,
      mat: matBeam, parent: root, collides: false,
    });
    box(scene, shadowGen, {
      name: 'stud', w: BEAM_W, h: STORY_H - 0.05, d: BEAM_W,
      x, y: SOCKEL_H + STORY_H / 2 + 0.025, z: -HOUSE_D / 2 - 0.16,
      mat: matBeam, parent: root, collides: false,
    });
  }
  // Diagonal-Streben in den Giebeln
  for (const side of [-1, 1]) {
    box(scene, shadowGen, {
      name: 'gable-brace', w: 0.16, h: 0.16, d: 1.6,
      x: side * (HOUSE_W / 2 - 1.5), y: SOCKEL_H + 2 * STORY_H + 0.4, z: 0,
      ry: Math.PI / 4 * side, mat: matBeam, parent: root, collides: false,
    });
  }

  // -- Satteldach
  const ridgeY = SOCKEL_H + 2 * STORY_H + ROOF_H;
  const roofY = (SOCKEL_H + 2 * STORY_H + ridgeY) / 2;
  const roofLen = HOUSE_W + 2 * ROOF_OVERHANG;
  const angle = Math.atan2(ROOF_H, HOUSE_D / 2);

  for (const side of [-1, 1]) {
    box(scene, shadowGen, {
      name: `roof-${side < 0 ? 'S' : 'N'}`, w: roofLen, h: 0.2, d: Math.sqrt(ROOF_H * ROOF_H + (HOUSE_D / 2) * (HOUSE_D / 2)),
      x: 0, y: roofY, z: (side * HOUSE_D) / 4,
      rx: side * angle, mat: matRoof, parent: root, collides: false,
    });
  }
  for (const side of [-1, 1]) {
    box(scene, shadowGen, {
      name: `gable-${side < 0 ? 'W' : 'E'}`, w: 0.15, h: ROOF_H, d: HOUSE_D + 2 * ROOF_OVERHANG,
      x: side * (HOUSE_W / 2 + ROOF_OVERHANG), y: SOCKEL_H + 2 * STORY_H + ROOF_H / 2, z: 0,
      mat: matPlaster, parent: root, collides: false,
    });
  }

  // -- Kamin (außen + Schornstein)
  // Issue #22: chimney was floating 2.5 m east of the indoor hearth
  // (which sits at world (0, _, -3.95)).  Place it directly above the
  // hearth on the outside face of the north wall so the smoke line is
  // visually continuous from the fire.
  const chimneyX = 0;
  const chimneyZ = -HOUSE_D / 2 - CHIMNEY_W / 2;
  box(scene, shadowGen, {
    name: 'chimney-base', w: CHIMNEY_W, h: CHIMNEY_H, d: CHIMNEY_W,
    x: chimneyX, y: CHIMNEY_H / 2, z: chimneyZ, mat: matStone, parent: root, collides: true,
  });
  box(scene, shadowGen, {
    name: 'chimney-cap', w: CHIMNEY_W + 0.2, h: 0.2, d: CHIMNEY_W + 0.2,
    x: chimneyX, y: CHIMNEY_H + 0.1, z: chimneyZ, mat: matStone, parent: root, collides: false,
  });

  // -- Tür (#19: Bretter, Eisenbänder, Studs, L-förmiger Griff) -----------
  const doorMesh = box(scene, shadowGen, {
    name: 'door-out', w: DOOR_W, h: DOOR_H, d: 0.06,
    x: 0, y: SOCKEL_H + DOOR_H / 2, z: HOUSE_D / 2 + 0.18,
    mat: matDoor, parent: root, collides: false, shadow: true,
  });
  // 3 vertikale Bretter (heller Streifen)
  for (let i = 0; i < 3; i++) {
    box(scene, shadowGen, {
      name: `door-plank-${i}`, w: DOOR_W / 3 - 0.04, h: DOOR_H - 0.08, d: 0.005,
      x: -DOOR_W / 2 + (i + 0.5) * (DOOR_W / 3), y: SOCKEL_H + DOOR_H / 2, z: HOUSE_D / 2 + 0.21,
      mat: matFurnWood, parent: root, collides: false, shadow: false,
    });
  }
  // 3 horizontale Eisenbänder
  for (const by of [SOCKEL_H + 0.25, SOCKEL_H + DOOR_H / 2, SOCKEL_H + DOOR_H - 0.25]) {
    box(scene, shadowGen, {
      name: 'door-band', w: DOOR_W + 0.04, h: 0.06, d: 0.02,
      x: 0, y: by, z: HOUSE_D / 2 + 0.21, mat: matIron, parent: root, collides: false,
    });
  }
  // Studs (4×3 = 12 Stück)
  for (const by of [SOCKEL_H + 0.28, SOCKEL_H + DOOR_H / 2 + 0.02, SOCKEL_H + DOOR_H - 0.28]) {
    for (const bx of [-DOOR_W / 2 + 0.1, DOOR_W / 2 - 0.14]) {
      box(scene, shadowGen, {
        name: 'door-stud', w: 0.06, h: 0.06, d: 0.025,
        x: bx, y: by, z: HOUSE_D / 2 + 0.22, mat: matIron, parent: root, collides: false,
      });
    }
  }
  // L-förmiger Griff (zwei kleine Boxen)
  box(scene, shadowGen, {
    name: 'door-handle-h', w: 0.04, h: 0.04, d: 0.04,
    x: DOOR_W / 2 - 0.12, y: SOCKEL_H + DOOR_H / 2, z: HOUSE_D / 2 + 0.22,
    mat: matIron, parent: root, collides: false,
  });
  box(scene, shadowGen, {
    name: 'door-handle-v', w: 0.04, h: 0.16, d: 0.04,
    x: DOOR_W / 2 - 0.18, y: SOCKEL_H + DOOR_H / 2 - 0.05, z: HOUSE_D / 2 + 0.22,
    mat: matIron, parent: root, collides: false,
  });
  // Tür-Rahmen
  for (const [lx, lw] of [[-DOOR_W / 2 - 0.1, 0.1], [DOOR_W / 2, 0.1]] as [number, number][]) {
    box(scene, shadowGen, {
      name: 'door-frame-side', w: lw, h: DOOR_H, d: 0.12,
      x: lx + lw / 2, y: SOCKEL_H + DOOR_H / 2, z: HOUSE_D / 2 + 0.16,
      mat: matBeam, parent: root, collides: false,
    });
  }
  box(scene, shadowGen, {
    name: 'door-frame-top', w: DOOR_W + 0.4, h: 0.15, d: 0.15,
    x: 0, y: SOCKEL_H + DOOR_H + 0.075, z: HOUSE_D / 2 + 0.16,
    mat: matBeam, parent: root, collides: false,
  });

  // -- Außen-Möbel: 2 Fässer + Holzstapel
  makeBarrel(scene, shadowGen, root, mats, 1.6, 0, -HOUSE_D / 2 - 0.7, 0.1);
  makeBarrel(scene, shadowGen, root, mats, 2.0, 0, -HOUSE_D / 2 - 1.1, -0.4);
  makeWoodpile(scene, shadowGen, root, mats, -HOUSE_W / 2 - 0.7, 0, -1.0);

  // ============================ INTERIOR ===================================
  interiorRoot.position.set(0, 0, 0);

  // Floor
  box(scene, shadowGen, {
    name: 'i-floor', w: I_W, h: 0.1, d: I_D,
    x: 0, y: 0.05, z: 0, mat: matFloor, parent: interiorRoot, collides: true,
  });
  // Sockelleisten
  for (const [wx, wz, ww, wd, rotY] of [
    [0, -I_D / 2, I_W, 0.05, 0],
    [0, I_D / 2, I_W, 0.05, 0],
    [I_W / 2, 0, 0.05, I_D, Math.PI / 2],
    [-I_W / 2, 0, 0.05, I_D, Math.PI / 2],
  ] as [number, number, number, number, number][]) {
    box(scene, shadowGen, {
      name: 'i-skirting', w: ww, h: 0.12, d: wd,
      x: wx, y: 0.12, z: wz, ry: rotY, mat: matBeam, parent: interiorRoot, collides: false,
    });
  }

  // Walls
  for (const [wname, wx, wz, ww, wd, rotY] of [
    ['i-wallN', 0, -I_D / 2, I_W, 0.15, 0],
    ['i-wallS', 0, I_D / 2, I_W, 0.15, 0],
    ['i-wallE', I_W / 2, 0, 0.15, I_D, Math.PI / 2],
    ['i-wallW', -I_W / 2, 0, 0.15, I_D, Math.PI / 2],
  ] as [string, number, number, number, number, number][]) {
    box(scene, shadowGen, {
      name: wname, w: ww, h: EG_H + OG_H + 0.4, d: wd,
      x: wx, y: (EG_H + OG_H + 0.4) / 2, z: wz, ry: rotY,
      mat: matPlaster, parent: interiorRoot, collides: true,
    });
  }

  // Mid-floor / OG-Boden — Issue #23.
  //
  // Issue #22 set this to a single thin box with `collides: false` so the
  // player could walk from the staircase onto the OG level.  But that left
  // no proper OG floor — the OG was effectively open to the rafters and
  // the player could drift up into the roof space.
  //
  // Now: split the mid-floor into two slabs that flank the staircase
  // opening, plus leave a 1.2 × 2.1 m hole directly above the stairs so
  // the staircase actually connects EG and OG.  Both slabs have
  // `collides: true` so the player has a real floor to stand on in the OG.
  //
  // Staircase footprint (interior coords):
  //   x in [-2.7, -1.7]   (width 1.0, centred on -2.2)
  //   z in [-3.0, -0.9]   (8 steps × 0.35 m step depth)
  //
  // We add a 0.1 m buffer on each side of the staircase footprint so
  // there's no risk of the player getting wedged between the stair
  // collision and the floor collision.
  const stairX0 = -2.7, stairX1 = -1.7;
  const stairZ0 = -3.0, stairZ1 = -0.9;
  const stairBuf = 0.1;
  const floorY = EG_H - 0.05;       // top of slab sits exactly at EG_H
  const floorH = 0.1;
  const floorDepth = I_D - 0.2;
  const floorZ = 0;
  // West slab (between west wall and staircase hole)
  const westW = (stairX0 - stairBuf) - (-I_W / 2);          // = -2.8 - (-3) = 0.2
  box(scene, shadowGen, {
    name: 'i-midfloor-W', w: westW, h: floorH, d: floorDepth,
    x: -I_W / 2 + westW / 2, y: floorY, z: floorZ,
    mat: matFloor, parent: interiorRoot, collides: true,
  });
  // East slab (between staircase hole and east wall)
  const eastW = (I_W / 2) - (stairX1 + stairBuf);           // = 3 - (-1.6) = 1.6
  box(scene, shadowGen, {
    name: 'i-midfloor-E', w: eastW, h: floorH, d: floorDepth,
    x: (stairX1 + stairBuf) + eastW / 2, y: floorY, z: floorZ,
    mat: matFloor, parent: interiorRoot, collides: true,
  });
  // North + south strips over the staircase so the OG floor is closed
  // everywhere except for the staircase opening itself.  Stair extends
  // from z=-3.0 to z=-0.9, so we leave a 2.1 m gap exactly over it.
  const stripW = (stairX1 + stairBuf) - (stairX0 - stairBuf); // = 1.2
  const stripX = (stairX0 + stairX1) / 2;                    // = -2.2
  const northDepth = (stairZ0 - stairBuf) - (-floorDepth / 2); // = -3.1 - (-4.4) = 1.3
  box(scene, shadowGen, {
    name: 'i-midfloor-N', w: stripW, h: floorH, d: northDepth,
    x: stripX, y: floorY, z: -floorDepth / 2 + northDepth / 2,
    mat: matFloor, parent: interiorRoot, collides: true,
  });
  const southDepth = (floorDepth / 2) - (stairZ1 + stairBuf); // = 4.4 - (-0.8) = 5.2
  box(scene, shadowGen, {
    name: 'i-midfloor-S', w: stripW, h: floorH, d: southDepth,
    x: stripX, y: floorY, z: (stairZ1 + stairBuf) + southDepth / 2,
    mat: matFloor, parent: interiorRoot, collides: true,
  });

  // Ceiling — Issue #23: raise it to y = EG_H + OG_H (3.0 + 2.8 = 5.8) so
  // it sits flush with the top of the interior walls (which have height
  // EG_H + OG_H + 0.4 = 6.2, centred at 3.1 → tops at 6.2).  Combined with
  // backFaceCulling=false on the gable materials this seals the OG from
  // the roof space.
  box(scene, shadowGen, {
    name: 'i-ceiling', w: I_W - 0.2, h: 0.1, d: I_D - 0.2,
    x: 0, y: EG_H + OG_H, z: 0, mat: matCeil, parent: interiorRoot, collides: false,
  });

  // Ceiling beams
  for (const storyY of [EG_H, EG_H + OG_H]) {
    for (let i = -1; i <= 1; i++) {
      box(scene, shadowGen, {
        name: `i-beam-${i + 1}-${storyY}`, w: I_W - 0.3, h: 0.2, d: 0.2,
        x: 0, y: storyY - 0.1, z: (i * (I_D - 0.3)) / 3,
        mat: matBeam, parent: interiorRoot, collides: false,
      });
    }
  }

  // Staircase mit Geländer (#20)
  makeStaircase(scene, shadowGen, interiorRoot, mats, -I_W / 2 + 0.8, 0, -3.0);

  // Interior door (visual)
  const interiorDoorMesh = box(scene, shadowGen, {
    name: 'i-door-out', w: DOOR_W, h: DOOR_H, d: 0.06,
    x: 0, y: DOOR_H / 2, z: I_D / 2 - 0.08,
    mat: matDoor, parent: interiorRoot, collides: false, shadow: true,
  });

  // ---- EG furniture ------------------------------------------------------
  makeHearth(scene, shadowGen, interiorRoot, mats, 0, 0, -I_D / 2 + 0.55);
  // Fire (emissive, animated)
  const fireMat = new StandardMaterial('mat-fire', scene);
  fireMat.diffuseColor = new Color3(1.0, 0.55, 0.15);
  fireMat.emissiveColor = new Color3(0.9, 0.45, 0.1);
  fireMat.specularColor = new Color3(0, 0, 0);
  const fireMesh = box(scene, shadowGen, {
    name: 'i-fire', w: 0.9, h: 0.6, d: 0.6,
    x: 0, y: 0.55, z: -I_D / 2 + 0.7, mat: fireMat, parent: interiorRoot,
    collides: false, shadow: false,
  });
  // Glühende Glut unter dem Feuer
  const emberMat = new StandardMaterial('mat-ember', scene);
  emberMat.diffuseColor = new Color3(0.6, 0.15, 0.05);
  emberMat.emissiveColor = new Color3(0.5, 0.1, 0.03);
  emberMat.specularColor = new Color3(0, 0, 0);
  box(scene, shadowGen, {
    name: 'i-ember', w: 1.0, h: 0.08, d: 0.65,
    x: 0, y: 0.16, z: -I_D / 2 + 0.7, mat: emberMat, parent: interiorRoot,
    collides: false, shadow: false,
  });

  makeTable(scene, shadowGen, interiorRoot, mats, 1.2, 0, -1.0);
  makeChair(scene, shadowGen, interiorRoot, mats, 1.2, 0, -0.05, Math.PI);
  makeChair(scene, shadowGen, interiorRoot, mats, 1.2, 0, -1.95, 0);
  makeChair(scene, shadowGen, interiorRoot, mats, 0.5, 0, -1.0, Math.PI / 2);
  makeShelf(scene, shadowGen, interiorRoot, mats, I_W / 2 - 0.3, 0, -2.5, -Math.PI / 2, 1.4, 1.6, 0.4, 3);
  makeChest(scene, shadowGen, interiorRoot, mats, -1.8, 0, I_D / 2 - 1.5, 0);
  makeBarrel(scene, shadowGen, interiorRoot, mats, -2.4, 0, 2.5, 0.2);
  makeBarrel(scene, shadowGen, interiorRoot, mats, -1.4, 0, 3.0, -0.1);
  makeWoodpile(scene, shadowGen, interiorRoot, mats, 2.4, 0, 3.0);

  // Teppich
  box(scene, shadowGen, {
    name: 'i-rug', w: 2.4, h: 0.02, d: 3.2,
    x: 0.5, y: 0.11, z: -1.0, ry: 0, mat: matRug, parent: interiorRoot,
    collides: false, shadow: false,
  });

  // Wandkerzen (3 Stück) — liefern Licht + sichtbare Kerze
  const candleMeshes: Mesh[] = [];
  const candlePositions: Array<[number, number]> = [
    [2.85, 1.7], [-2.85, 1.7], [0, -I_D / 2 + 0.35],
  ];
  for (const [cx, cz] of candlePositions) {
    const sc = makeWallSconce(scene, shadowGen, interiorRoot, mats, cx, 1.5, cz);
    candleMeshes.push(sc.mesh);
  }

  // Wandregale an den Wänden (2 kleine Regale)
  box(scene, shadowGen, {
    name: 'i-wallshelf-1', w: 0.8, h: 0.04, d: 0.25,
    x: -2.6, y: 1.6, z: -1.0, ry: Math.PI / 2, mat: matFurnWood, parent: interiorRoot,
    collides: false,
  });
  // Kleine Töpfe auf dem Regal
  for (let i = 0; i < 3; i++) {
    box(scene, shadowGen, {
      name: 'pot', w: 0.12, h: 0.1, d: 0.12,
      x: -2.5, y: 1.7, z: -1.2 + i * 0.25, mat: matStraw, parent: interiorRoot,
      collides: false,
    });
  }

  // ---- OG furniture ------------------------------------------------------
  makeBed(scene, shadowGen, interiorRoot, mats, 1.2, EG_H, -I_D / 2 + 1.4, Math.PI / 2);
  makeChair(scene, shadowGen, interiorRoot, mats, -1.5, EG_H, -I_D / 2 + 1.2, Math.PI / 2);
  makeChest(scene, shadowGen, interiorRoot, mats, -1.5, EG_H, 0.5, 0);
  makeShelf(scene, shadowGen, interiorRoot, mats, I_W / 2 - 0.3, EG_H, -2.5, -Math.PI / 2, 1.4, 1.6, 0.4, 3);

  // ============================ LIGHTING (#21) =============================
  // 1) Kamin-PointLight (warmes Orange)
  const hearthLight = new PointLight(
    'l-hearth',
    new Vector3(0, 1.4, -I_D / 2 + 0.9),
    scene
  );
  hearthLight.diffuse = new Color3(1.0, 0.55, 0.18);
  hearthLight.specular = new Color3(0.4, 0.2, 0.05);
  hearthLight.intensity = 1.1;
  hearthLight.range = HEARTH_LIGHT_RANGE;
  hearthLight.parent = interiorRoot;

  // 2) Candle point-lights (3 Stück, kleines Flackern)
  const candleLights = candlePositions.map(([cx, cz], i) => {
    const l = new PointLight(
      `l-candle-${i}`,
      new Vector3(cx, 1.7, cz),
      scene
    );
    l.diffuse = new Color3(1.0, 0.78, 0.32);
    l.specular = new Color3(0.3, 0.2, 0.05);
    l.intensity = 0.6;
    l.range = CANDLE_LIGHT_RANGE;
    l.parent = interiorRoot;
    return l;
  });

  // 3) Tageslicht durch Fenster — 4 SpotLights (eines pro OG-Fenster, da
  // EG-Fenster hinter dem Wand-Versatz schwer zu treffen sind)
  const windowSpots: { light: any; spec: typeof windowSpecs[number] }[] = [];
  const ogWindows = windowSpecs.filter((w) => w.isOG);
  for (let i = 0; i < Math.min(ogWindows.length, 4); i++) {
    const w = ogWindows[i];
    const l = new SpotLight(
      `l-win-${i}`,
      // Origin slightly outside the window, pointing into the room.
      new Vector3(w.x - Math.sin(w.rotY) * 0.4, w.y, w.z - Math.cos(w.rotY) * 0.4),
      new Vector3(Math.sin(w.rotY), -0.5, Math.cos(w.rotY)),
      Math.PI / 4, // cone angle
      4, // exponent
      scene
    );
    l.diffuse = new Color3(0.9, 0.92, 1.0);
    l.specular = new Color3(0.1, 0.1, 0.15);
    l.intensity = 0.45;
    l.range = WINDOW_LIGHT_RANGE;
    l.parent = interiorRoot;
    windowSpots.push({ light: l, spec: w });
  }

  // ---- Door highlight materials ------------------------------------------
  const matDoorHighlight = new StandardMaterial('mat-door-highlight', scene);
  matDoorHighlight.diffuseTexture = matDoor.diffuseTexture;
  matDoorHighlight.diffuseColor = new Color3(0.6, 0.45, 0.15);
  matDoorHighlight.emissiveColor = new Color3(0.4, 0.25, 0.05);
  matDoorHighlight.specularColor = new Color3(0, 0, 0);
  let doorHighlight: 'world' | 'interior' | null = null;

  // ---- Per-frame update for fire pulse + candle flicker -------------------
  let elapsed = 0;
  const update = (dtSec: number) => {
    elapsed += dtSec;
    // Fire pulse: intensity oscillates 0.8 – 1.2× with a slow LFO + noise.
    const pulse = 0.95 + 0.25 * Math.sin(elapsed * 2.4) + 0.05 * Math.sin(elapsed * 11.0);
    fireMat.emissiveColor.set(0.9 * pulse, 0.45 * pulse, 0.1 * pulse);
    fireMesh.position.x = Math.sin(elapsed * 6.0) * 0.04;
    // Hearth light follows the pulse.
    hearthLight.intensity = 0.85 + 0.35 * pulse;
    // Candle flicker: fast noise on intensity.
    for (let i = 0; i < candleLights.length; i++) {
      const cl = candleLights[i];
      const flick = 0.85 + 0.15 * Math.sin(elapsed * (8 + i * 1.3) + i * 1.7)
                          + 0.05 * Math.sin(elapsed * 19.0 + i);
      cl.intensity = 0.55 * flick;
    }
  };

  // ---- Interior root parented + hidden by default ------------------------
  interiorRoot.parent = root;
  setNodeEnabled(interiorRoot, false);

  return {
    root,
    doorMesh,
    interiorRoot,
    interiorDoorMesh,
    interiorLights: [hearthLight, ...candleLights, ...windowSpots.map((s) => s.light)],
    dispose() {
      root.dispose(false, true);
      interiorRoot.dispose(false, true);
    },
    setDoorHighlight(state: 'world' | 'interior' | null) {
      if (state === doorHighlight) return;
      doorHighlight = state;
      if (state === null) {
        doorMesh.material = matDoor;
        interiorDoorMesh.material = matDoor;
      } else if (state === 'world') {
        doorMesh.material = matDoorHighlight;
        interiorDoorMesh.material = matDoor;
      } else {
        doorMesh.material = matDoor;
        interiorDoorMesh.material = matDoorHighlight;
      }
    },
    setInteriorVisible(visible: boolean) {
      setNodeEnabled(interiorRoot, visible);
    },
    update,
  };
}

function setNodeEnabled(node: TransformNode, enabled: boolean): void {
  node.setEnabled(enabled);
  const children = node.getChildMeshes();
  for (const c of children) c.setEnabled(enabled);
}